// redux/ugc/commentsSlice.ts
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { commentsApi, Paginated, RootCommentDTO } from "../../services/ugc/commentsApi";
import {
  CommentDTO,
  CommentCreatePayload,
  CommentUpdatePayload,
  CommentSummary,
} from "../../types/ugc/commentsTypes";
import { RootState, AppDispatch } from "../storeTypes";
import {
  onCommentEvent,
  ensureCommentsSocketConnected,
  subscribeCommentsThread,
  unsubscribeCommentsThread,
} from "../../realtime/commentsSocket";

/** ---------------------------------------------
 * Helpers
 * ----------------------------------------------*/

// Thread key (contentType#objectId)
const makeThreadKey = (contentType: string, objectId: number) =>
  `${contentType}#${objectId}`;

// Ensure an item has responses array (for backward-compat rendering)
const ensureReplies = (c: CommentDTO) => {
  if (!c.responses) c.responses = [];
  return c;
};

// Dedupe by id
const dedupeById = <T extends { id: number }>(arr: T[]) => {
  const seen = new Set<number>();
  return arr.filter((x) => (seen.has(x.id) ? false : (seen.add(x.id), true)));
};

// ensure thread state object exists + initialize maps safely
const ensureThread = (state: CommentsState, key: string): ThreadState => {
  if (!state.threads[key]) {
    state.threads[key] = makeDefaultThreadState();
  } else {
    // defensive: backfill missing maps if structure came from older state
    const thr = state.threads[key]!;
    if (!thr.repliesPage) thr.repliesPage = {};
    if (!thr.repliesCount) thr.repliesCount = {};
    if (!thr.expandedReplies) thr.expandedReplies = {};
    if (thr.page === undefined) thr.page = 1;
    if (thr.hasMore === undefined) thr.hasMore = false;
    if (thr.pagingLoading === undefined) thr.pagingLoading = false;
    if (thr.loading === undefined) thr.loading = false;
  }
  return state.threads[key]!;
};


// Remove temp duplicates for same author+text under same parent
const purgeMatchingTemps = (list: CommentDTO[], real: CommentDTO) => {
  const isSame = (t: CommentDTO) =>
    t.id < 0 &&
    t.name?.id === real.name?.id &&
    (t.recomment || null) === (real.recomment || null) &&
    (t.comment || "").trim() === (real.comment || "").trim();

  // roots
  for (let i = list.length - 1; i >= 0; i--) {
    const it = list[i];
    if (!it.recomment && isSame(it)) list.splice(i, 1);
    // replies
    if (it.responses && it.responses.length) {
      for (let j = it.responses.length - 1; j >= 0; j--) {
        const r = it.responses[j] as any as CommentDTO;
        if (isSame({ ...r, recomment: it.id })) it.responses.splice(j, 1);
      }
    }
  }
};

// Upsert a root (replace if exists, else unshift)
const upsertRoot = (thread: ThreadState, dto: CommentDTO) => {
  const idx = thread.items.findIndex((i) => i.id === dto.id);
  if (idx >= 0) {
    // keep already-loaded replies if present
    const existingReplies = thread.items[idx].responses;
    thread.items[idx] = ensureReplies(dto);
    if (existingReplies && existingReplies.length) {
      thread.items[idx].responses = existingReplies;
    }
  } else {
    thread.items.unshift(ensureReplies(dto));
  }
};

// Upsert a reply under its parent
const upsertReply = (thread: ThreadState, dto: CommentDTO) => {
  const parent = thread.items.find((i) => i.id === dto.recomment);
  if (!parent) return;
  parent.responses = parent.responses || [];
  const ridx = parent.responses.findIndex((r) => r.id === dto.id);
  const compact = (d: CommentDTO) => ({
    id: d.id,
    comment: d.comment,
    published_at: d.published_at,
    is_active: d.is_active,
    name: d.name,
  });
  if (ridx >= 0) {
    parent.responses[ridx] = compact(dto);
  } else {
    parent.responses.push(compact(dto));
  }
};

// Merge non-paginated server thread (legacy) with temps
const mergeServerThread = (existing: CommentDTO[], server: CommentDTO[]) => {
  const map = new Map<number, CommentDTO>();
  server.forEach((s) => map.set(s.id, ensureReplies(s)));
  const out: CommentDTO[] = [...map.values()];
  existing.forEach((e) => {
    if (e.id < 0) {
      const dup = server.some(
        (s) =>
          s.name?.id === e.name?.id &&
          (s.recomment || null) === (e.recomment || null) &&
          (s.comment || "").trim() === (e.comment || "").trim()
      );
      if (!dup) out.unshift(ensureReplies(e));
    }
  });
  const roots = dedupeById(out);
  roots.forEach((r) => {
    if (r.responses && r.responses.length) {
      // @ts-ignore
      r.responses = dedupeById(r.responses);
    }
  });
  return roots;
};

/** ---------------------------------------------
 * State
 * ----------------------------------------------*/

type RepliesPageState = {
  page: number;
  next: string | null;
  hasMore: boolean;
  loading: boolean;
  expanded: boolean; // UI toggle: show/hide replies
};

export type ThreadState = {
  // roots always live here
  items: CommentDTO[];
  loading: boolean; // initial/legacy loading
  error?: string | null;
  summary?: CommentSummary;
  subscribed?: boolean;

  // ---- roots pagination ----
  page?: number;
  next?: string | null;
  hasMore?: boolean;
  pagingLoading?: boolean; // loading "page" for roots

  // ---- replies pagination per root ----
  repliesPage: Record<number, RepliesPageState>; // by parentId
  repliesCount: Record<number, number>; // parentId -> total replies reported by server (replies_count)
  expandedReplies?: Record<number, boolean>;
};

type JumpState = {
  targetCommentId: number | null;
  parentId: number | null;
  jumpedOnce: boolean;
};

export type CommentsState = {
  threads: Record<string, ThreadState>;
  wsBound: boolean;
  jumpState: JumpState;
};

const initialThread = (): ThreadState => ({
  items: [],
  loading: false,
  repliesPage: {},
  repliesCount: {},
});

const initialState: CommentsState = {
  threads: {},
  wsBound: false,
  jumpState: {
    targetCommentId: null,
    parentId: null,
    jumpedOnce: false,
  },
};

// --- Default per-root replies paging state factory ---
const makeDefaultRepliesPageState = (): RepliesPageState => ({
  page: 1,            // first page already loaded when you render thread
  next: null,         // no cursor yet
  hasMore: false,     // safe default; real value comes from server
  loading: false,     // not loading by default
  expanded: false,    // UI: replies collapsed by default
});

// --- Default thread state factory (always returns a fully-formed ThreadState) ---
const makeDefaultThreadState = (): ThreadState => ({
  items: [],
  loading: false,
  error: null,
  summary: undefined,
  subscribed: false,

  // roots pagination
  page: 1,
  next: null,
  hasMore: false,
  pagingLoading: false,

  // replies pagination
  repliesPage: {},     // parentId -> RepliesPageState
  repliesCount: {},    // parentId -> number
  expandedReplies: {}, // parentId -> boolean
});


/** module-scope WS unbinder (bind once) */
let unbindWsListener: (() => void) | null = null;

/** ---------------------------------------------
 * Thunks — Non-paginated (kept for backward compat)
 * ----------------------------------------------*/

export const fetchCommentsThread = createAsyncThunk<
  { key: string; items: CommentDTO[] },
  { contentType: string; objectId: number }
>("comments/fetchThread", async ({ contentType, objectId }) => {
  const items = await commentsApi.listThreadByObject(contentType, objectId);
  return { key: makeThreadKey(contentType, objectId), items };
});

export const fetchCommentsSummary = createAsyncThunk<
  { key: string; summary: CommentSummary },
  { contentType: string; objectId: number }
>("comments/fetchSummary", async ({ contentType, objectId }) => {
  const summary = await commentsApi.summaryByObject(contentType, objectId);
  return { key: makeThreadKey(contentType, objectId), summary };
});

/** ---------------------------------------------
 * Thunks — NEW pagination (roots + replies)
 * ----------------------------------------------*/

/** Fetch first/any page of ROOTS (no replies payload). */
export const fetchRootPage = createAsyncThunk<
  { key: string; page: number; data: Paginated<RootCommentDTO> },
  { contentType: string; objectId: number; page?: number; pageSize?: number }
>("comments/fetchRootPage", async ({ contentType, objectId, page = 1, pageSize = 10 }) => {
  const data = await commentsApi.listThreadPage(contentType, objectId, page, pageSize);
  return { key: makeThreadKey(contentType, objectId), page, data };
});

/** Load more ROOTS (based on thread.next/hasMore). */
export const loadMoreRoots = createAsyncThunk<
  { key: string; nextPage: number; data: Paginated<RootCommentDTO> },
  { contentType: string; objectId: number; pageSize?: number },
  { state: RootState }
>("comments/loadMoreRoots", async ({ contentType, objectId, pageSize = 10 }, { getState, rejectWithValue }) => {
  const key = makeThreadKey(contentType, objectId);
  const thread = getState().comments.threads[key];
  if (!thread || !thread.hasMore) {
    return rejectWithValue("No more pages");
  }
  const nextPage = (thread.page || 1) + 1;
  const data = await commentsApi.listThreadPage(contentType, objectId, nextPage, pageSize);
  return { key, nextPage, data };
});

/** Fetch first/any page of REPLIES for a parent (and mark expanded=true). */
export const fetchRepliesPage = createAsyncThunk<
  { key: string; parentId: number; page: number; data: Paginated<CommentDTO> },
  { contentType: string; objectId: number; parentId: number; page?: number; pageSize?: number }
>("comments/fetchRepliesPage", async ({ contentType, objectId, parentId, page = 1, pageSize = 10 }) => {
  const data = await commentsApi.listRepliesPage(parentId, page, pageSize);
  return { key: makeThreadKey(contentType, objectId), parentId, page, data };
});

/** Load more REPLIES for a parent. */
export const loadMoreReplies = createAsyncThunk<
  { key: string; parentId: number; nextPage: number; data: Paginated<CommentDTO> },
  { contentType: string; objectId: number; parentId: number; pageSize?: number },
  { state: RootState }
>("comments/loadMoreReplies", async ({ contentType, objectId, parentId, pageSize = 10 }, { getState, rejectWithValue }) => {
  const key = makeThreadKey(contentType, objectId);
  const thread = getState().comments.threads[key];
  const rstate = thread?.repliesPage[parentId];
  if (!rstate || !rstate.hasMore) {
    return rejectWithValue("No more replies");
  }
  const nextPage = (rstate.page || 1) + 1;
  const data = await commentsApi.listRepliesPage(parentId, nextPage, pageSize);
  return { key, parentId, nextPage, data };
});

/** ---------------------------------------------
 * CRUD Thunks (unchanged)
 * ----------------------------------------------*/

export const createComment = createAsyncThunk<
  CommentDTO,
  { payload: CommentCreatePayload }
>("comments/create", async ({ payload }, { rejectWithValue }) => {
  try {
    const saved = await commentsApi.create(payload);
    return saved;
  } catch (e: any) {
    return rejectWithValue(e?.message || "Failed to create comment");
  }
});

export const updateComment = createAsyncThunk<
  CommentDTO,
  { id: number; payload: Partial<CommentUpdatePayload> }
>("comments/update", async ({ id, payload }, { rejectWithValue }) => {
  try {
    const saved = await commentsApi.update(id, payload);
    return saved;
  } catch (e: any) {
    return rejectWithValue(e?.message || "Failed to update comment");
  }
});

export const deleteComment = createAsyncThunk<{ id: number }, { id: number }>(
  "comments/delete",
  async ({ id }, { rejectWithValue }) => {
    try {
      const ok = await commentsApi.remove(id);
      if (!ok) throw new Error("Not deleted");
      return { id };
    } catch (e: any) {
      return rejectWithValue(e?.message || "Failed to delete comment");
    }
  }
);

/** ----------------------------------------------------
 * Jump to a comment or reply across ALL paginated pages
 * -----------------------------------------------------*/
export const jumpToComment = createAsyncThunk<
  { found: boolean; parentId?: number | null },
  {
    contentType: string;
    objectId: number;
    commentId: number;
    parentId?: number;
    pageSize?: number;
  },
  { state: RootState }
>(
  "comments/jumpToComment",
  async (
    { contentType, objectId, commentId, parentId, pageSize = 10 },
    { dispatch }
  ) => {
    const maxRootPages = 50;

    try {
      /** =========================
       * CASE A – root comment
       * ========================= */
      if (!parentId) {
        let page = 1;

        while (page <= maxRootPages) {
          const payload = await dispatch(
            fetchRootPage({ contentType, objectId, page, pageSize })
          ).unwrap();

          const foundRoot = payload.data.results.find(
            (r: any) => r.id === commentId
          );

          if (foundRoot) {
            return { found: true, parentId: null };
          }

          if (!payload.data.next) break;
          page += 1;
        }
        return { found: false };
      }

      /** =========================
       * CASE B – reply (با parentId)
       * فقط parent root را پیدا می‌کنیم
       * ========================= */
      let page = 1;

      while (page <= maxRootPages) {
        const payload = await dispatch(
          fetchRootPage({ contentType, objectId, page, pageSize })
        ).unwrap();

        const foundRoot = payload.data.results.find(
          (r: any) => r.id === parentId
        );

        if (foundRoot) {
          return { found: true, parentId };
        }

        if (!payload.data.next) break;
        page += 1;
      }

      return { found: false };
    } catch (err) {
      return { found: false };
    }
  }
);



/** ---------------------------------------------
 * Realtime binding (unchanged)
 * ----------------------------------------------*/

export const ensureCommentsRealtime =
  (params: {
    token: string;
    contentTypeId: number;
    objectId: number;
    contentType: string;
  }) =>
    (dispatch: AppDispatch, getState: () => RootState) => {
      const state = getState().comments as CommentsState;

      // 1) connect WS (idempotent)
      ensureCommentsSocketConnected(params.token);

      // 2) global event binding (once)
      if (!state.wsBound) {
        if (unbindWsListener) {
          try {
            unbindWsListener();
          } catch { }
          unbindWsListener = null;
        }

        unbindWsListener = onCommentEvent(({ event, data }) => {
          if (event === "created") {
            dispatch(wsCommentCreated({ dto: data as CommentDTO }));
          } else if (event === "updated") {
            dispatch(wsCommentUpdated({ dto: data as CommentDTO }));
          } else if (event === "deleted") {
            dispatch(wsCommentDeleted({ id: (data as any)?.id }));
          }
        });

        dispatch(markWsBound());
      }

      // 3) subscribe this thread
      const key = makeThreadKey(params.contentType, params.objectId);
      const thread = state.threads[key];
      if (!thread?.subscribed) {
        subscribeCommentsThread(params.contentTypeId, params.objectId);
        dispatch(markThreadSubscribed({ key }));
      }
    };

export const leaveCommentsRealtime =
  (params: { contentTypeId: number; objectId: number; contentType: string }) =>
    (dispatch: AppDispatch) => {
      unsubscribeCommentsThread(params.contentTypeId, params.objectId);
      dispatch(
        markThreadUnsubscribed({
          key: makeThreadKey(params.contentType, params.objectId),
        })
      );
    };

/** ---------------------------------------------
 * Slice
 * ----------------------------------------------*/

const commentsSlice = createSlice({
  name: "comments",
  initialState,
  reducers: {
    // mark that global WS listener is attached
    markWsBound(state) {
      state.wsBound = true;
    },
    // local thread subscription flag
    markThreadSubscribed(state, action: PayloadAction<{ key: string }>) {
      state.threads[action.payload.key] =
        state.threads[action.payload.key] || initialThread();
      state.threads[action.payload.key].subscribed = true;
    },
    markThreadUnsubscribed(state, action: PayloadAction<{ key: string }>) {
      if (state.threads[action.payload.key]) {
        state.threads[action.payload.key].subscribed = false;
      }
    },

    /** UI toggle: expand/collapse replies for a root */
    toggleReplies(state, action: PayloadAction<{ key: string; parentId: number; expanded?: boolean }>) {
      const { key, parentId, expanded } = action.payload;
      const thread = state.threads[key] || (state.threads[key] = initialThread());
      const prev = thread.repliesPage[parentId] || {
        page: 0,
        next: null,
        hasMore: true,
        loading: false,
        expanded: false,
      };
      thread.repliesPage[parentId] = {
        ...prev,
        expanded: expanded != null ? expanded : !prev.expanded,
      };
    },

    /* ------------ WS reducers (dup guards) ------------ */

    wsCommentCreated(state, action: PayloadAction<{ dto: CommentDTO }>) {
      const dto = action.payload.dto;
      const key = makeThreadKey(dto.content_type, dto.object_id);
      const thread = state.threads[key] || (state.threads[key] = initialThread());

      // skip if already exists
      const existsRoot = thread.items.some((i) => i.id === dto.id);
      const existsReply = thread.items.some((p) =>
        (p.responses || []).some((r) => r.id === dto.id)
      );
      if (existsRoot || existsReply) return;

      // purge matching temps
      if (dto.id > 0) purgeMatchingTemps(thread.items, dto);

      // upsert
      if (dto.recomment) {
        upsertReply(thread, dto);

        // ✅ bump repliesCount if we track it
        const pid = dto.recomment;
        if (pid) {
          const cur = thread.repliesCount[pid] || 0;
          thread.repliesCount[pid] = cur + 1;
        }
      } else {
        upsertRoot(thread, dto);
      }

      // patch summary if present
      if (thread.summary) {
        if (dto.recomment) thread.summary.replies += 1;
        else thread.summary.comments += 1;
        thread.summary.total += 1;
      }
    },

    wsCommentUpdated(state, action: PayloadAction<{ dto: CommentDTO }>) {
      const dto = action.payload.dto;
      const key = makeThreadKey(dto.content_type, dto.object_id);
      const thread = state.threads[key];
      if (!thread) return;

      if (dto.recomment) {
        upsertReply(thread, dto);
      } else {
        upsertRoot(thread, dto);
      }
    },

    wsCommentDeleted(state, action: PayloadAction<{ id: number }>) {
      const id = action.payload.id;

      Object.values(state.threads).forEach((thread) => {
        // roots
        const rIdx = thread.items.findIndex((i) => i.id === id);
        if (rIdx >= 0) {
          // adjust repliesCount map cleanup for this parent if any
          delete thread.repliesPage[id];
          delete thread.repliesCount[id];

          const removed = thread.items.splice(rIdx, 1)[0];
          if (thread.summary) {
            if (removed.recomment) thread.summary.replies -= 1;
            else thread.summary.comments -= 1;
            thread.summary.total -= 1;
          }
          return;
        }
        // replies
        thread.items.forEach((p) => {
          if (!p.responses) return;
          const cIdx = p.responses.findIndex((r) => r.id === id);
          if (cIdx >= 0) {
            p.responses.splice(cIdx, 1);
            // ✅ decrement repliesCount for that parent
            const cur = thread.repliesCount[p.id] || 0;
            if (cur > 0) thread.repliesCount[p.id] = cur - 1;

            if (thread.summary) {
              thread.summary.replies -= 1;
              thread.summary.total -= 1;
            }
          }
        });
      });
    },

    /** Optimistic temp insert (negative id) */
    optimisticAdd(
      state,
      action: PayloadAction<{
        key: string;
        draft: CommentDTO;
      }>
    ) {
      const { key, draft } = action.payload;
      const thread = state.threads[key] || (state.threads[key] = initialThread());

      if (draft.recomment) {
        const p = thread.items.find((i) => i.id === draft.recomment);
        if (p) {
          p.responses = p.responses || [];
          p.responses.push({
            id: draft.id,
            comment: draft.comment,
            published_at: draft.published_at,
            is_active: draft.is_active,
            name: draft.name,
          });
          // bump repliesCount UI-side
          const cur = thread.repliesCount[p.id] || 0;
          thread.repliesCount[p.id] = cur + 1;
        }
      } else {
        thread.items.unshift(ensureReplies(draft));
      }
    },

    /** Replace temp (negative id) with real DTO */
    replaceTemp(
      state,
      action: PayloadAction<{ key: string; tempId: number; saved: CommentDTO }>
    ) {
      const { key, tempId, saved } = action.payload;
      const thread = state.threads[key];
      if (!thread) return;

      // roots
      const idx = thread.items.findIndex((i) => i.id === tempId);
      if (idx >= 0) {
        thread.items[idx] = ensureReplies(saved);
        return;
      }
      // replies
      thread.items.forEach((p) => {
        if (!p.responses) return;
        const rIdx = p.responses.findIndex((r) => r.id === tempId);
        if (rIdx >= 0) {
          p.responses[rIdx] = {
            id: saved.id,
            comment: saved.comment,
            published_at: saved.published_at,
            is_active: saved.is_active,
            name: saved.name,
          };
        }
      });
    },

    setRepliesExpanded(
      state,
      action: PayloadAction<{ key: string; rootId: number; expanded: boolean }>
    ) {
      const { key, rootId, expanded } = action.payload;
      const thr = ensureThread(state, key);
      thr.expandedReplies![rootId] = !!expanded;
    },

    toggleRepliesExpanded(
      state,
      action: PayloadAction<{ key: string; rootId: number }>
    ) {
      const { key, rootId } = action.payload;
      const thr = ensureThread(state, key);
      const cur = !!thr.expandedReplies![rootId];
      thr.expandedReplies![rootId] = !cur;
    },

    resetRepliesExpanded(state, action: PayloadAction<{ key: string }>) {
      const { key } = action.payload;
      const thr = ensureThread(state, key);
      thr.expandedReplies = {};
    },

    setJumpState(
      state,
      action: PayloadAction<Partial<JumpState>>
    ) {
      state.jumpState = {
        ...state.jumpState,
        ...action.payload,
      };
    },

    resetJumpState(state) {
      state.jumpState = {
        targetCommentId: null,
        parentId: null,
        jumpedOnce: false,
      };
    },

  },
  extraReducers: (builder) => {
    /* ========== Legacy thread (no pagination) ========== */
    builder
      .addCase(fetchCommentsThread.pending, (state, action) => {
        const { contentType, objectId } = action.meta.arg;
        const key = makeThreadKey(contentType, objectId);
        const thread = ensureThread(state, key);          // ✅ always full shape
        thread.loading = true;
        thread.error = null;
      })
      .addCase(fetchCommentsThread.fulfilled, (state, action) => {
        const { key, items } = action.payload;
        const thread = ensureThread(state, key);          // ✅
        thread.items = mergeServerThread(thread.items, items);
        thread.loading = false;
      })
      .addCase(fetchCommentsThread.rejected, (state, action) => {
        const { contentType, objectId } = action.meta.arg;
        const key = makeThreadKey(contentType, objectId);
        const thread = ensureThread(state, key);          // ✅
        thread.loading = false;
        thread.error = action.error.message || "Failed to fetch thread";
      });

    /* ========== Summary ========== */
    builder.addCase(fetchCommentsSummary.fulfilled, (state, action) => {
      const { key, summary } = action.payload;
      const thread = ensureThread(state, key);            // ✅
      thread.summary = summary;
    });

    /* ========== Create / Update / Delete ========== */
    builder
      .addCase(createComment.fulfilled, (state, action) => {
        const saved = action.payload;
        const key = makeThreadKey(saved.content_type, saved.object_id);
        const thread = ensureThread(state, key);          // ✅

        // already present?
        const existsRoot = thread.items.some((i) => i.id === saved.id);
        const existsReply = thread.items.some((p) =>
          (p.responses || []).some((r) => r.id === saved.id)
        );
        if (existsRoot || existsReply) return;

        // purge temp drafts that match
        purgeMatchingTemps(thread.items, saved);

        if (saved.recomment) {
          upsertReply(thread, saved);
          const pid = saved.recomment!;
        } else {
          upsertRoot(thread, saved);
        }
      })
      .addCase(updateComment.fulfilled, (state, action) => {
        const saved = action.payload;
        const key = makeThreadKey(saved.content_type, saved.object_id);
        const thread = ensureThread(state, key);          // ✅

        if (saved.recomment) upsertReply(thread, saved);
        else upsertRoot(thread, saved);
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        const { id } = action.payload;

        // search all threads (defensive, in case a comment appears in multiple views)
        Object.values(state.threads).forEach((thr) => {
          const thread = ensureThread(state, makeThreadKey(
            // we don't have ct/oid here; ensureThread will just backfill structure
            // pass a dummy key won't work; instead operate on 'thr' directly
            // NOTE: we already have 'thr', so no key needed here.
            "" as any, 0 as any
          )) || thr;

          // Try remove root
          const rootIdx = thr.items.findIndex((i) => i.id === id);
          if (rootIdx >= 0) {
            const removed = thr.items[rootIdx];
            // clean per-root reply paging maps
            delete thr.repliesPage[removed.id];
            delete thr.repliesCount[removed.id];
            thr.items.splice(rootIdx, 1);
            return;
          }

          // Try remove reply
          thr.items.forEach((p) => {
            if (!p.responses) return;
            const rIdx = p.responses.findIndex((r) => r.id === id);
            if (rIdx >= 0) {
              p.responses.splice(rIdx, 1);
              const cur = thr.repliesCount[p.id] || 0;
              if (cur > 0) thr.repliesCount[p.id] = cur - 1;
            }
          });
        });
      });

    /* ========== ROOTS pagination ========== */
    builder
      .addCase(fetchRootPage.pending, (state, action) => {
        const { contentType, objectId } = action.meta.arg;
        const key = makeThreadKey(contentType, objectId);
        const thread = ensureThread(state, key);          // ✅
        thread.pagingLoading = true;                      // first-page or refresh
        thread.error = null;
      })
      .addCase(fetchRootPage.fulfilled, (state, action) => {
        const { key, page, data } = action.payload;
        const thread = ensureThread(state, key);          // ✅

        // map minimal root DTOs (responses lazy)
        const mapped: CommentDTO[] = data.results.map((r) =>
          ensureReplies({
            id: r.id,
            name: r.name,
            comment: r.comment,
            published_at: r.published_at,
            is_active: r.is_active,
            content_type: ({} as any),
            object_id: ({} as any),
            recomment: undefined,
            responses: [],
          } as CommentDTO)
        );

        if (page === 1) {
          thread.items = mapped;                          // replace on first page
        } else {
          thread.items = dedupeById([...thread.items, ...mapped]);
        }

        // track replies_count for each root
        data.results.forEach((r) => {
          thread.repliesCount[r.id] = r.replies_count ?? 0;
        });

        thread.page = page;
        thread.next = data.next;
        // hasMore based on how many roots we currently hold vs total count
        thread.hasMore = (thread.items.length < (data.count ?? thread.items.length));
        thread.pagingLoading = false;
      })
      .addCase(fetchRootPage.rejected, (state, action) => {
        const { contentType, objectId } = action.meta.arg;
        const key = makeThreadKey(contentType, objectId);
        const thread = ensureThread(state, key);          // ✅
        thread.pagingLoading = false;
        thread.error = action.error.message || "Failed to fetch comments";
      })

      .addCase(loadMoreRoots.pending, (state, action) => {
        const { contentType, objectId } = action.meta.arg;
        const key = makeThreadKey(contentType, objectId);
        const thread = ensureThread(state, key);          // ✅
        thread.pagingLoading = true;
      })
      .addCase(loadMoreRoots.fulfilled, (state, action) => {
        const { key, nextPage, data } = action.payload;
        const thread = ensureThread(state, key);          // ✅

        const mapped: CommentDTO[] = data.results.map((r) =>
          ensureReplies({
            id: r.id,
            name: r.name,
            comment: r.comment,
            published_at: r.published_at,
            is_active: r.is_active,
            content_type: ({} as any),
            object_id: ({} as any),
            recomment: undefined,
            responses: [],
          } as CommentDTO)
        );

        thread.items = dedupeById([...thread.items, ...mapped]);

        data.results.forEach((r) => {
          // keep latest replies_count (fallback to existing value)
          thread.repliesCount[r.id] = r.replies_count ?? (thread.repliesCount[r.id] || 0);
        });

        thread.page = nextPage;
        thread.next = data.next;
        thread.hasMore = (thread.items.length < (data.count ?? thread.items.length));
        thread.pagingLoading = false;
      })
      .addCase(loadMoreRoots.rejected, (state, action) => {
        const { contentType, objectId } = action.meta.arg;
        const key = makeThreadKey(contentType, objectId);
        const thread = ensureThread(state, key);          // ✅
        thread.pagingLoading = false;
        thread.error = action.error.message || "Failed to load more";
      });

    /* ========== REPLIES pagination ========== */
    builder
      .addCase(fetchRepliesPage.pending, (state, action) => {
        const { contentType, objectId, parentId } = action.meta.arg;
        const key = makeThreadKey(contentType, objectId);
        const thread = ensureThread(state, key);          // ✅

        const prev = thread.repliesPage[parentId] || makeDefaultRepliesPageState();
        thread.repliesPage[parentId] = { ...prev, loading: true, expanded: true };
      })
      .addCase(fetchRepliesPage.fulfilled, (state, action) => {
        const { key, parentId, page, data } = action.payload;
        const thread = ensureThread(state, key);

        const compacted = data.results.map((d) => ({
          id: d.id,
          comment: d.comment,
          published_at: d.published_at,
          is_active: d.is_active,
          name: d.name,
        })) as CommentDTO[];

        const parent = thread.items.find((i) => i.id === parentId);
        const current = (parent?.responses || []) as CommentDTO[];
        const merged = page === 1 ? compacted : dedupeById([...current, ...compacted]);

        if (parent) parent.responses = merged;

        thread.repliesPage[parentId] = {
          page,
          next: data.next,
          hasMore: Boolean(data.next),
          loading: false,
          expanded: true,
        };

        thread.repliesCount[parentId] = data.count ?? merged.length;
      })
      .addCase(fetchRepliesPage.rejected, (state, action) => {
        const { contentType, objectId, parentId } = action.meta.arg;
        const key = makeThreadKey(contentType, objectId);
        const thread = ensureThread(state, key);          // ✅

        const prev = thread.repliesPage[parentId] || makeDefaultRepliesPageState();
        thread.repliesPage[parentId] = { ...prev, loading: false };
      })

      .addCase(loadMoreReplies.pending, (state, action) => {
        const { contentType, objectId, parentId } = action.meta.arg;
        const key = makeThreadKey(contentType, objectId);
        const thread = ensureThread(state, key);          // ✅

        const prev = thread.repliesPage[parentId] || makeDefaultRepliesPageState();
        thread.repliesPage[parentId] = { ...prev, loading: true };
      })
      .addCase(loadMoreReplies.fulfilled, (state, action) => {
        const { key, parentId, nextPage, data } = action.payload;
        const thread = ensureThread(state, key);          // ✅

        const compacted = data.results.map((d) => ({
          id: d.id,
          comment: d.comment,
          published_at: d.published_at,
          is_active: d.is_active,
          name: d.name,
        })) as CommentDTO[];

        const parent = thread.items.find((i) => i.id === parentId);
        const current = (parent?.responses || []) as CommentDTO[];
        const merged = dedupeById([...current, ...compacted]);

        if (parent) parent.responses = merged;

        thread.repliesPage[parentId] = {
          page: nextPage,
          next: data.next,
          hasMore: (merged.length < (data.count ?? merged.length)),
          loading: false,
          expanded: true,
        };

        thread.repliesCount[parentId] = data.count ?? merged.length;
      })
      .addCase(loadMoreReplies.rejected, (state, action) => {
        const { contentType, objectId, parentId } = action.meta.arg;
        const key = makeThreadKey(contentType, objectId);
        const thread = ensureThread(state, key);          // ✅

        const prev = thread.repliesPage[parentId] || makeDefaultRepliesPageState();
        thread.repliesPage[parentId] = { ...prev, loading: false };
      })

      .addCase(jumpToComment.pending, (state) => {
        // while searching — optional but useful
        // we don't clear jumpState here
      })
      .addCase(jumpToComment.fulfilled, (state, action) => {
        const { found, parentId } = action.payload;

        if (found) {
          state.jumpState = {
            jumpedOnce: true,
            targetCommentId: action.meta.arg.commentId,
            parentId: parentId ?? null,
          };
        }
      })
      .addCase(jumpToComment.rejected, (state) => {
        // nothing special here
      });

  },

});

/** ---------------------------------------------
 * Exports
 * ----------------------------------------------*/

export const {
  markWsBound,
  markThreadSubscribed,
  markThreadUnsubscribed,
  wsCommentCreated,
  wsCommentUpdated,
  wsCommentDeleted,
  optimisticAdd,
  replaceTemp,
  toggleReplies,
  setRepliesExpanded,
  toggleRepliesExpanded,
  resetRepliesExpanded,
  setJumpState, resetJumpState
} = commentsSlice.actions;

export default commentsSlice.reducer;
